import { PublicKey } from "@solana/web3.js";
import { RESOURCE_REGISTRY, type ResourceId } from "../../backend/src/economy/generatedGameplayRules";

export { RESOURCE_REGISTRY };
export type ScannerAllocation = { wallet: string; resourceId: ResourceId; quantity: number };
export type ScannerPromo = { code: string; resourceId: ResourceId; quantity: number; maximumUses: number; claimedUses: number; expiresAt: string; status: string };
export type ScannerGrant = { batchId: string; allocations: ScannerAllocation[]; actor: string; reason: string; grantedAt: string };
export type ScannerClaim = { wallet: string; resourceId: ResourceId; quantity: number; claimedAt: string };
export type ScannerPage<T> = { items: T[]; nextCursor: string | null };
export const scannerName = (id: string) => id.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");

export function validateScannerAllocations(rows: readonly { wallet: string; resourceId: string; quantity: number }[]): ScannerAllocation[] {
  if (!rows.length || rows.length > 40) throw Error("Use 1–40 wallet/resource rows per import.");
  const seen = new Set<string>();
  return rows.map((row, index) => {
    const wallet = row.wallet.trim(), resourceId = RESOURCE_REGISTRY.find(resource => resource.id === row.resourceId.trim())?.id;
    try { const key = new PublicKey(wallet); if (key.toBase58() !== wallet || key.equals(PublicKey.default) || !PublicKey.isOnCurve(key.toBytes())) throw Error(); }
    catch { throw Error(`Row ${index + 1}: invalid wallet address.`); }
    if (!resourceId) throw Error(`Row ${index + 1}: unknown resourceId.`);
    if (!Number.isSafeInteger(row.quantity) || row.quantity < 1 || row.quantity > 1000) throw Error(`Row ${index + 1}: quantity must be a whole number from 1 to 1,000.`);
    const key = `${wallet}:${resourceId}`;
    if (seen.has(key)) throw Error(`Row ${index + 1}: duplicate wallet and resource. Combine their quantities first.`);
    seen.add(key); return { wallet, resourceId, quantity: row.quantity };
  });
}

/** RFC-style quoted fields, escaped quotes, CRLF and UTF-8 BOM; exact named columns. */
export function parseScannerCsv(text: string): ScannerAllocation[] {
  if (text.length > 64_000) throw Error("CSV is too large. Import up to 40 rows at a time.");
  const records: string[][] = [], row: string[] = [];
  let field = "", quoted = false, closed = false;
  const source = text.replace(/^\uFEFF/, "");
  const cell = () => { row.push(field.trim()); field = ""; closed = false; };
  const line = () => { cell(); if (row.some(Boolean)) records.push([...row]); row.length = 0; };
  for (let i = 0; i < source.length; i++) {
    const char = source[i]!;
    if (quoted) {
      if (char === '"' && source[i + 1] === '"') { field += '"'; i++; }
      else if (char === '"') { quoted = false; closed = true; }
      else field += char;
    } else if (char === '"' && !field && !closed) quoted = true;
    else if (char === ",") cell();
    else if (char === "\r" || char === "\n") { line(); if (char === "\r" && source[i + 1] === "\n") i++; }
    else if (char === '"' || closed && char.trim()) throw Error("Malformed CSV quoting.");
    else field += char;
  }
  if (quoted) throw Error("CSV has an unclosed quoted field.");
  line();
  const headers = records.shift();
  if (!headers || headers.length !== 3 || new Set(headers).size !== 3 || !["wallet", "resourceId", "quantity"].every(name => headers.includes(name)))
    throw Error("CSV headers must be wallet,resourceId,quantity.");
  return validateScannerAllocations(records.map((cells, index) => {
    if (cells.length !== 3) throw Error(`Row ${index + 1}: expected three columns.`);
    const quantity = cells[headers.indexOf("quantity")]!;
    if (!/^[1-9][0-9]*$/.test(quantity)) throw Error(`Row ${index + 1}: quantity must be a positive whole number.`);
    return { wallet: cells[headers.indexOf("wallet")]!, resourceId: cells[headers.indexOf("resourceId")]!, quantity: Number(quantity) };
  }));
}
